import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { doSignOut } from "../firebase/auth"
import { useAuth } from "../contexts/authContext"
import { Dropdown } from "antd"
import { DownOutlined } from "@ant-design/icons"
import logo from "../assets/images/logo.svg"
import defaultAvatar from "../assets/images/default-avatar.png"
import "../assets/styles/Navbar.css"
import { getMyUser } from "../services/AuthServices"

/**
 * Navigation bar component for the application.
 * Includes logo, language selector, and user profile.
 *
 * @returns {JSX.Element} The Navbar component
 */
function Navbar() {
  const navigate = useNavigate()
  const { userLoggedIn, currentUser, getToken } = useAuth()
  const { t, i18n } = useTranslation()
  const [avatarSrc, setAvatarSrc] = useState(defaultAvatar)
  const [isAdmin, setIsAdmin] = useState(false) 

  useEffect(() => {
    const checkUserRole = async () => {
      if (!userLoggedIn || !currentUser) return

      try {
        const token = await getToken()
        if (!token) return
        const userData = await getMyUser(token)
        if (userData.role && userData.role.name === "admin") {
          setIsAdmin(true)
        }
      } catch (error) {
        console.error("Error al verificar el rol del usuario:", error)
      }
    }

    checkUserRole()
  }, [userLoggedIn, currentUser, getToken])

  useEffect(() => {
    if (currentUser?.photoURL) {
      const img = new Image()
      img.onload = () => {
        setAvatarSrc(currentUser.photoURL)
      }
      img.onerror = () => {
        console.error("Failed to load photo URL, using default avatar.")
        setAvatarSrc(defaultAvatar)
      }
      img.src = currentUser.photoURL
    } else {
      setAvatarSrc(defaultAvatar)
    }
  }, [currentUser])

  /**
   * Handles user logout.
   * Clears storage and navigates to login page.
   */
  const handleLogout = async () => {
    await doSignOut()
    localStorage.clear()
    sessionStorage.clear()
    navigate("/login")
  }

  /**
   * Handles click on the logo.
   * Navigates to the home page.
   */
  const handleLogoClick = () => {
    navigate("/")
  }

  /**
   * Changes the application language.
   *
   * @param {string} lng - The language code to change to
   */
  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng)
  }

  const userMenu = (
    <div className="custom-dropdown-menu">
      <button onClick={() => navigate("/dashboard")}>{t("navbar.myStudies")}</button>
      <div className="divider"></div>
      <button onClick={() => navigate("/editmyreport")}>{t("navbar.editPdfReport")}</button>
      {isAdmin && (
        <>
          <div className="divider"></div>
          <button onClick={() => navigate("/admin")}>{t("navbar.adminPanel")}</button>
        </>
      )}
      <div className="divider"></div>
      <button onClick={handleLogout}>{t("navbar.logout")}</button>
    </div>
  )

  const languageMenu = (
    <div className="custom-dropdown-menu language-menu">
      <button className={i18n.language === "es" ? "active-language" : ""} onClick={() => changeLanguage("es")}>
        <span className="flag-icon">🇪🇸</span>
        <span>{t("navbar.spanish")}</span>
      </button>
      <button className={i18n.language === "en" ? "active-language" : ""} onClick={() => changeLanguage("en")}>
        <span className="flag-icon">🇺🇸</span>
        <span>{t("navbar.english")}</span>
      </button>
    </div>
  )

  return (
    <nav className="navbar">
      <div className="navbar-logo-container" onClick={handleLogoClick}>
        <img src={logo || "/placeholder.svg"} alt="Logo" className="navbar-logo" />
      </div>

      <div className="navbar-actions">
        <Dropdown overlay={languageMenu} trigger={["click"]} placement="bottomRight">
          <div className="language-selector">
            <span className="flag-icon current-flag">{i18n.language === "es" ? "🇪🇸" : "🇺🇸"}</span>
            <DownOutlined className="dropdown-arrow" />
          </div>
        </Dropdown>

        {userLoggedIn && currentUser && (
          <Dropdown overlay={userMenu} trigger={["click"]} placement="bottomRight">
            <div className="user-profile">
              <div className="user-info">
                <div className="user-avatar-name">
                  <img
                    src={avatarSrc || "/placeholder.svg"}
                    alt="User Avatar"
                    className="user-avatar"
                    referrerPolicy="no-referrer"
                  />
                  <span className="user-name">{currentUser.displayName || t("navbar.user")}</span>
                </div>
                <DownOutlined className="dropdown-arrow" />
              </div>
            </div>
          </Dropdown>
        )}
      </div>
    </nav>
  )
}

export default Navbar
